import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { ErmSdkModule } from 'erm-sdk';
import { EnvironmentUiService } from './services/environment-ui.service';

type EnvUiState = 'ready' | 'not-ready' | 'unavailable';

@Component({
  standalone: true,
  imports: [CommonModule, ErmSdkModule],
  selector: 'app-environment-launch',
  template: `
@if (uiService.environment$()) {
  <!-- Environment panel -->
  <section
    class="tw-relative tw-flex tw-items-stretch tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white"
  >
    <!-- Left colored rail -->
    <div class="tw-w-3 tw-rounded-l-xl" [ngClass]="railColor$()"></div>

    <div class="tw-flex tw-items-center tw-justify-between tw-flex-1 tw-px-2 tw-py-2">
      <div>
        <div class="tw-text-base tw-font-medium tw-text-slate-900">Environment</div>
        <div class="tw-text-sm tw-font-medium" [ngClass]="statusColor$()">
          {{ statusMessage$() }}
        </div>
      </div>

      <button
        type="button"
        [disabled]="!uiService.canLaunch$()"
        (click)="envButtonClick()"
        class="tw-h-10 tw-px-4 tw-rounded-md tw-font-semibold tw-shadow-sm tw-inline-flex tw-items-center tw-gap-2"
        [ngClass]="buttonClass$()"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="tw-w-5 tw-h-5 tw-fill-current" aria-hidden="true">
          <path d="M192 384L88.5 384C63.6 384 48.3 356.9 61.1 335.5L114 247.3C122.7 232.8 138.3 224 155.2 224L250.2 224C326.3 95.1 439.8 88.6 515.7 99.7C528.5 101.6 538.5 111.6 540.3 124.3C551.4 200.2 544.9 313.7 416 389.8L416 484.8C416 501.7 407.2 517.3 392.7 526L304.5 578.9C283.2 591.7 256 576.3 256 551.5L256 448C256 412.7 227.3 384 192 384L191.9 384zM464 224C464 197.5 442.5 176 416 176C389.5 176 368 197.5 368 224C368 250.5 389.5 272 416 272C442.5 272 464 250.5 464 224z"/>
        </svg>
        {{ envButtonLabel$() }}
      </button>
    </div>
  </section>
}
@else {
  <div class="tw-flex tw-items-center tw-justify-center tw-h-full">
    <p>No environment provided</p>
  </div>
}
`,
})
export class EnvironmentLaunchComponent {
  private readonly router = inject(Router);
  public readonly uiService = inject(EnvironmentUiService);

  /**
   * Normalize service flags to a small UI state we can map from.
   */
  private readonly envState$: Signal<EnvUiState> = computed(() => {
    const canLaunch = this.uiService.canLaunch$();
    const isFunctional = this.uiService.isFunctional$();

    if (!isFunctional) {
      return 'unavailable';
    }

    return canLaunch ? 'ready' : 'not-ready';
  });

  // Texts
  public readonly envButtonLabel$: Signal<string> = computed(() => {
    switch (this.envState$()) {
      case 'ready':
        return 'Launch';
      case 'not-ready':
        return 'Not Ready';
      case 'unavailable':
        return 'Unavailable';
    }
  });

  public readonly statusMessage$: Signal<string> = computed(() => {
    switch (this.envState$()) {
      case 'ready':
        return 'Ready';
      case 'not-ready':
        return 'Not Ready';
      case 'unavailable':
        return 'Unavailable';
    }
  });

  // Colors / classes
  public readonly railColor$: Signal<string> = computed(() => {
    switch (this.envState$()) {
      case 'ready':
        return 'tw-bg-green-600';
      case 'not-ready':
        return 'tw-bg-orange-400';
      case 'unavailable':
        return 'tw-bg-gray-400';
    }
  });

  public readonly statusColor$: Signal<string> = computed(() => {
    switch (this.envState$()) {
      case 'ready':
        return 'tw-text-green-600';
      case 'not-ready':
        return 'tw-text-orange-400';
      case 'unavailable':
        return 'tw-text-gray-400';
    }
  });

  public readonly buttonClass$: Signal<string> = computed(() => {
    // Keep disabled style explicit for clarity; hover only when actionable.
    const canLaunch = this.uiService.canLaunch$();
    return canLaunch
      ? 'tw-bg-green-600 hover:tw-bg-green-700 tw-text-white'
      : 'tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400';
  });

  public envButtonClick(): void {
    const environment = this.uiService.environment$();

    if (!environment?.environmentId) {
      return;
    }

    this.router.navigate(['/erm/launch/client', environment.environmentId]);
  }
}
